import { FC, MouseEventHandler, useCallback, useMemo, useState } from 'react';

import { EquipmentType, TransactionDamage } from 'types/damage';

import { Transaction } from 'types';
import ImageViewer from './image';

const Notes: FC<Pick<TransactionDamage, 'notes'>> = ({ notes }) => {
  const [notesClass, setNotesClass] = useState('notes text-ellipsis');
  const toggleNotes = useCallback(() => {
    if (!notesClass) return;
    setNotesClass(p => (!p.includes('text-ellipsis') ? 'notes text-ellipsis' : 'notes'));
  }, [notesClass, setNotesClass]);
  /* disable hover effect for short notes */
  const handleHover: MouseEventHandler<HTMLParagraphElement> = useCallback(
    e =>
      notesClass &&
      notesClass !== 'notes' &&
      e.target instanceof HTMLParagraphElement &&
      e.target.offsetWidth >= e.target.scrollWidth &&
      setNotesClass(''),
    [notesClass, setNotesClass]
  );
  return (
    <p key={notesClass} className={notesClass} onClick={toggleNotes} onMouseEnter={handleHover}>
      {notes ?? 'N/A'}
    </p>
  );
};

const DamageRow: FC<{ damage: TransactionDamage }> = ({ damage }) => {
  const images = useMemo(() => {
    const { imageBase64, imageKey } = { ...damage };
    if (!imageBase64 && !imageKey) return [];
    else return [{ imageBase64, imageKey }];
  }, [damage]);

  const damageLocation = useMemo(() => {
    const { location, part, subLocation } = { ...damage };
    return [location, part, subLocation].filter(s => s?.length).join(' | ');
  }, [damage]);

  return (
    <>
      <div className="col-span-2">
        <h6 className="text-dark font-weight-regular">LOCATION</h6>
        <p className="text-black">{damageLocation ?? 'N/A'}</p>
      </div>
      <div>
        <h6 className="text-dark font-weight-regular">DAMAGE</h6>
        <p className="text-black">{damage.damageType ?? 'N/A'}</p>
      </div>
      <div className="col-span-2">
        <h6 className="text-dark font-weight-regular">NOTES</h6>
        <Notes {...damage} />
      </div>
      <div className="d-flex flex-column align-items-center">
        <h6 className="text-dark font-weight-regular">IMAGE</h6>
        <ImageViewer images={images}>VIEW DAMAGE</ImageViewer>
      </div>
    </>
  );
};

const InspectionSummary: FC<Pick<Transaction, 'damages'>> = transaction => {
  const damages: Map<EquipmentType, TransactionDamage[]> = useMemo(() => {
    return Object.assign([], transaction.damages).reduce((acc, val: TransactionDamage) => {
      if (!acc.has(val.equipmentType)) return acc.set(val.equipmentType, [val]);
      return acc.set(val.equipmentType, [...acc.get(val.equipmentType), val]);
    }, new Map());
  }, [transaction.damages]);

  if (!damages.size) return null;

  return (
    <div className="card-primary">
      <div className="card-header px-3">
        <h4>INSPECTION SUMMARY</h4>
      </div>
      <div className="card-body">
        {damages.has(EquipmentType.CONTAINER) && (
          <div>
            <h5 className="primary-dark">CONTAINER</h5>
            <div className="grid-6 p-2">
              {damages.get(EquipmentType.CONTAINER)?.map(d => (
                <DamageRow key={d.id} damage={d} />
              ))}
            </div>
          </div>
        )}
        {damages.has(EquipmentType.CHASSIS) && (
          <div>
            <h5 className="primary-dark">CHASSIS</h5>
            <div className="grid-6 p-2">
              {damages.get(EquipmentType.CHASSIS)?.map(d => (
                <DamageRow key={d.id} damage={d} />
              ))}
            </div>
          </div>
        )}
        {damages.has(EquipmentType.GENSET) && (
          <div>
            <h5 className="primary-dark">GENSET</h5>
            <div className="grid-6 p-2">
              {damages.get(EquipmentType.GENSET)?.map(d => (
                <DamageRow key={d.id} damage={d} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InspectionSummary;
